/**
 * Chummys product blocks — <brownie-options> "Fill Your Box" + <gift-addon>
 *
 * Fulfilment-critical custom elements for the Concept theme (v6.0.0).
 * - <brownie-options>: a visual box builder — tap flavour chips to drop
 *   hand-drawn brownie tiles into box cells (capacity = variant box max);
 *   tap a filled cell to remove it. Writes ONE line-item property in the
 *   exact live format " 2x Kinder Bueno, 1x Oreo" (leading space is a live
 *   fulfilment artifact — byte-identical on purpose; counts serialized in
 *   FLAVOUR LIST ORDER, non-zero only). Variant limits from a Liquid-rendered
 *   JSON map; syncs on the form's non-bubbling `variant:change`.
 * - <gift-addon>: `name="bundles"` checkbox (Concept batch-adds the gift-tag
 *   product with the main product in ONE cart call) + To / Message / From
 *   fields mirrored into hidden properties on the MAIN line; once-per-session
 *   "no gift message?" modal; unchecks itself after `cartUpdate` like the
 *   theme's own product-bundle-details.
 *
 * One document-level CAPTURE submit listener vetoes both the AJAX and native
 * submit paths (it runs before Concept's at-target ProductForm handler) and
 * also covers the sticky buy bar, which submits the same form.
 */
(() => {
  'use strict';

  if (customElements.get('brownie-options')) return;

  /** formId -> { picker, gift } */
  const registry = new Map();

  const register = (kind, element) => {
    const formId = element.getAttribute('form');
    if (!formId) return;
    const group = registry.get(formId) || {};
    group[kind] = element;
    registry.set(formId, group);
  };

  const unregister = (kind, element) => {
    const formId = element.getAttribute('form');
    const group = registry.get(formId);
    if (group && group[kind] === element) delete group[kind];
  };

  const readJson = (element, selector) => {
    try {
      const node = element.querySelector(selector);
      return node ? JSON.parse(node.textContent) : null;
    } catch (error) {
      return null;
    }
  };

  const sessionFlags = {};
  const sessionGet = (key) => {
    try {
      return sessionStorage.getItem(key) !== null || sessionFlags[key] === true;
    } catch (error) {
      return sessionFlags[key] === true;
    }
  };
  const sessionSet = (key) => {
    sessionFlags[key] = true;
    try {
      sessionStorage.setItem(key, '1');
    } catch (error) {
      /* private mode — in-memory flag already set */
    }
  };

  class BrownieOptions extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.limits = readJson(this, '[data-box-limits]') || {};
      this.propertyInput = this.querySelector('[data-property-input]');
      this.errorElement = this.querySelector('[data-error]');
      this.totalElement = this.querySelector('[data-total]');
      this.maxElement = this.querySelector('[data-max]');
      this.progressFill = this.querySelector('[data-progress-fill]');
      this.summaryElement = this.querySelector('[data-summary]');
      this.boxElement = this.querySelector('[data-box]');
      this.tileTemplate = this.querySelector('[data-tile-template]');
      this.chips = Array.from(this.querySelectorAll('[data-flavour-chip]'));

      /** Ordered picks: { flavour, palette } per occupied cell. */
      this.picks = [];

      const initial = readJson(this, '[data-initial-limits]') || {};
      this.applyLimits(this.currentLimits() || initial);

      // QA flag: any variant whose limits resolve to 0 will fail closed at
      // submit time — surface the misconfiguration loudly during testing.
      Object.keys(this.limits).forEach((variantId) => {
        const limits = this.limits[variantId];
        if (!limits || !(limits.max > 0)) {
          console.warn(
            `[chummys] brownie-options: variant ${variantId} resolves box max 0 ` +
            '(missing custom.box_max metafield and no digit in the variant title) — ' +
            'add-to-cart will be blocked for it.'
          );
        }
      });

      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => this.addFlavour(chip), { signal });
      });

      // Filled cells are remove buttons (delegated — cells are rebuilt often).
      if (this.boxElement) {
        this.boxElement.addEventListener('click', (event) => {
          const cell = event.target.closest('[data-cell]');
          if (!cell || cell.disabled) return;
          const index = parseInt(cell.dataset.cellIndex, 10);
          if (!Number.isNaN(index)) this.removeAt(index);
        }, { signal });
      }

      // `variant:change` is dispatched ON the form and does not bubble.
      if (this.productForm) {
        this.productForm.addEventListener('variant:change', (event) => {
          this.onVariantChange(event.detail && event.detail.variant);
        }, { signal });
      }

      register('picker', this);
      this.rebuildCells();
      this.sync();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      unregister('picker', this);
    }

    get formId() {
      return this.getAttribute('form');
    }

    get productForm() {
      return document.forms[this.formId];
    }

    get currentVariantId() {
      const input = this.productForm && this.productForm.querySelector('input[name="id"]');
      return input ? input.value : null;
    }

    currentLimits() {
      const variantId = this.currentVariantId;
      return variantId ? this.limits[variantId] : null;
    }

    applyLimits(limits) {
      this.max = limits && limits.max > 0 ? limits.max : 0;
      this.min = limits && limits.min > 0 ? limits.min : 0;
      if (this.maxElement && this.max > 0) this.maxElement.textContent = this.max;
    }

    onVariantChange(variant) {
      const limits = (variant && this.limits[variant.id]) || this.currentLimits();
      if (limits) this.applyLimits(limits);
      // Box size changed: keep the earliest picks that still fit.
      if (this.max > 0 && this.picks.length > this.max) {
        this.picks.length = this.max;
      }
      this.rebuildCells();
      this.sync();
      // ProductInfo clears form error messages on variant change — clear ours
      // too when the new limits make the current picks valid again.
      if (this.validate()) this.clearError();
    }

    /** counts per flavour in CHIP (flavour list) ORDER — serialization order matters. */
    counts() {
      return this.chips.map((chip) => ({
        chip,
        flavour: chip.dataset.flavour,
        count: this.picks.filter((pick) => pick.flavour === chip.dataset.flavour).length
      }));
    }

    total() {
      return this.picks.length;
    }

    addFlavour(chip) {
      if (this.max > 0 && this.total() >= this.max) {
        this.showError();
        this.pulseBox();
        return;
      }
      this.picks.push({ flavour: chip.dataset.flavour, palette: chip.dataset.palette || '0' });
      this.renderPicks(this.picks.length - 1);
      this.sync();
      if (this.validate()) this.clearError();
    }

    removeAt(index) {
      if (index < 0 || index >= this.picks.length) return;
      this.picks.splice(index, 1);
      this.renderPicks();
      this.sync();
      if (this.validate()) this.clearError();
    }

    /** Build empty cells to match capacity (3 cols up to 6, else 4). */
    rebuildCells() {
      if (!this.boxElement) return;
      const capacity = this.max > 0 ? this.max : 6;
      this.boxElement.style.setProperty('--box-cols', capacity <= 6 ? 3 : 4);
      this.boxElement.innerHTML = '';
      for (let i = 0; i < capacity; i += 1) {
        const cell = document.createElement('button');
        cell.type = 'button';
        cell.className = 'brownie-options__cell';
        cell.dataset.cell = '';
        cell.dataset.cellIndex = i;
        cell.disabled = true;
        cell.setAttribute('aria-label', `Slot ${i + 1} — empty`);
        this.boxElement.appendChild(cell);
      }
      this.renderPicks();
    }

    /**
     * Paint picks into cells. When `animateIndex` is given only that cell
     * gets the drop-in animation (fresh pick); full repaints stay calm.
     */
    renderPicks(animateIndex) {
      if (!this.boxElement || !this.tileTemplate) return;
      const cells = Array.from(this.boxElement.querySelectorAll('[data-cell]'));
      cells.forEach((cell, index) => {
        const pick = this.picks[index];
        const had = cell.dataset.flavour;
        if (!pick) {
          if (had !== undefined) {
            delete cell.dataset.flavour;
            cell.className = 'brownie-options__cell';
            cell.innerHTML = '';
            cell.disabled = true;
            cell.setAttribute('aria-label', `Slot ${index + 1} — empty`);
          }
          return;
        }
        if (had === pick.flavour && cell.dataset.palette === pick.palette) return;
        cell.dataset.flavour = pick.flavour;
        cell.dataset.palette = pick.palette;
        cell.className = `brownie-options__cell brownie-options__cell--filled brownie-options__cell--v${pick.palette}`;
        cell.innerHTML = '';
        cell.appendChild(this.tileTemplate.content.cloneNode(true));
        cell.disabled = false;
        cell.title = `${pick.flavour} — tap to remove`;
        cell.setAttribute('aria-label', `Remove ${pick.flavour} from slot ${index + 1}`);
        if (index === animateIndex) {
          cell.classList.add('brownie-options__cell--landing');
          cell.addEventListener('animationend', () => {
            cell.classList.remove('brownie-options__cell--landing');
          }, { once: true });
        }
      });
    }

    pulseBox() {
      if (!this.boxElement) return;
      this.boxElement.classList.remove('brownie-options__box--pulse');
      // restart the animation
      void this.boxElement.offsetWidth; // eslint-disable-line no-void
      this.boxElement.classList.add('brownie-options__box--pulse');
    }

    /**
     * Live fulfilment format: " 2x Kinder Bueno, 1x Oreo" — non-zero only,
     * in flavour-list order. NOTE the LEADING SPACE: real live orders
     * (verified via Admin API, #221508–#221522) all carry it (artifact of the
     * live theme's dev.js string building) and packing slips/fulfilment read
     * this property — keep the bytes identical.
     */
    propertyValue() {
      const value = this.counts()
        .filter((entry) => entry.count > 0)
        .map((entry) => `${entry.count}x ${entry.flavour}`)
        .join(', ');
      return value === '' ? '' : ` ${value}`;
    }

    sync() {
      const total = this.total();
      if (this.totalElement) this.totalElement.textContent = total;

      const value = this.propertyValue();
      if (this.propertyInput) {
        this.propertyInput.value = value;
        this.propertyInput.disabled = value === '';
      }

      if (this.progressFill && this.max > 0) {
        this.progressFill.style.width = `${Math.min(100, Math.round((total / this.max) * 100))}%`;
      }

      if (this.summaryElement) {
        const summary = this.counts()
          .filter((entry) => entry.count > 0)
          .map((entry) => `${entry.count}× ${entry.flavour}`)
          .join(' · ');
        this.summaryElement.textContent = summary ? `Your box: ${summary}` : '';
        this.summaryElement.hidden = summary === '';
      }

      const full = this.max > 0 && total >= this.max;
      this.counts().forEach(({ chip, count }) => {
        const badge = chip.querySelector('[data-chip-badge]');
        if (badge) {
          badge.textContent = count;
          badge.hidden = count === 0;
        }
        chip.disabled = full;
        chip.classList.toggle('brownie-options__chip--active', count > 0);
      });

      this.classList.toggle('brownie-options--full', full);
    }

    validate() {
      // Fail CLOSED: a product that rendered the picker but whose variant
      // resolves max=0 (no custom.box_max metafield AND no digit in the
      // variant title) is misconfigured — blocking here prevents orders with
      // zero flavours and no fulfilment property.
      if (this.max <= 0) return false;
      const total = this.total();
      return total >= this.min && total <= this.max;
    }

    errorMessage() {
      if (this.max <= 0) return 'Flavour selection is unavailable for this option right now — please contact us.';
      if (this.min === this.max) return `Please choose ${this.max} flavours`;
      return `Please choose between ${this.min} and ${this.max} flavours`;
    }

    showError() {
      if (!this.errorElement) return;
      this.errorElement.textContent = this.errorMessage();
      this.errorElement.hidden = false;
      this.classList.add('brownie-options--invalid');
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.hidden = true;
      this.classList.remove('brownie-options--invalid');
    }
  }

  class GiftAddon extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.checkbox = this.querySelector('[data-gift-checkbox]');
      this.fieldsWrapper = this.querySelector('[data-gift-fields]');
      this.errorElement = this.querySelector('[data-gift-error]');
      this.modal = this.querySelector('[data-gift-modal]');
      this.fields = Array.from(this.querySelectorAll('[data-gift-field]'));
      this.pendingSubmitter = null;

      if (!this.checkbox) return;

      this.checkbox.addEventListener('change', () => {
        this.toggleFields();
        this.syncProperties();
        this.dispatchBundleChange();
      }, { signal });

      this.fields.forEach((field) => {
        field.addEventListener('input', () => {
          this.updateCounter(field);
          this.syncProperties();
        }, { signal });
      });

      const addButton = this.querySelector('[data-gift-modal-add]');
      if (addButton) {
        addButton.addEventListener('click', () => this.onModalAdd(), { signal });
      }
      const skipButton = this.querySelector('[data-gift-modal-skip]');
      if (skipButton) {
        skipButton.addEventListener('click', () => this.onModalSkip(), { signal });
      }
      // the sheet's own fields: live card preview + counter, then copied onto the page fields on "add & continue"
      this.modalFields = this.modal ? Array.from(this.modal.querySelectorAll('[data-gift-modal-field]')) : [];
      this.modalFields.forEach((field) => {
        field.addEventListener('input', () => { this.previewModal(); if (field.value.trim()) this.clearModalError(); }, { signal });
        field.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' && field.tagName !== 'TEXTAREA') { event.preventDefault(); this.onModalAdd(); }
        }, { signal });
      });
      if (this.modal) {
        this.modal.addEventListener('modal:afterHide', () => {
          document.body.classList.remove('chummys-gift-open');
          if (this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus({ preventScroll: true });
        }, { signal });
      }

      // Mimic product-bundle-details: uncheck after a successful product-form
      // add. Concept's ERROR branch also publishes cartUpdate with source
      // 'product-form' (after a cartError) — gate on that so a failed add
      // (e.g. sold-out variant) doesn't hide the customer's filled gift fields.
      this.lastAddFailed = false;
      const pubsub = window.theme && window.theme.pubsub;
      if (pubsub) {
        this.unsubscribeCartError = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartError, (event) => {
          if (event && event.source === 'product-form') this.lastAddFailed = true;
        });
        this.unsubscribeCartUpdate = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'product-form') {
            const failed = this.lastAddFailed;
            this.lastAddFailed = false;
            if (!failed) this.onCartAdded();
          }
        });
      } else {
        document.addEventListener('ajaxProduct:added', () => this.onCartAdded(), { signal });
      }

      register('gift', this);
      this.toggleFields();
      this.syncProperties();
      this.fields.forEach((field) => this.updateCounter(field));
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.unsubscribeCartUpdate) this.unsubscribeCartUpdate();
      if (this.unsubscribeCartError) this.unsubscribeCartError();
      unregister('gift', this);
    }

    get formId() {
      return this.getAttribute('form');
    }

    get productForm() {
      return document.forms[this.formId];
    }

    get checked() {
      return !!(this.checkbox && this.checkbox.checked && !this.checkbox.disabled);
    }

    toggleFields() {
      if (!this.fieldsWrapper) return;
      this.fieldsWrapper.hidden = !this.checked;
      this.checkbox.setAttribute('aria-expanded', this.checked ? 'true' : 'false');
      if (!this.checked) this.clearFieldError();
    }

    updateCounter(field) {
      const container = field.closest('.gift-addon__field');
      const count = container && container.querySelector('[data-count]');
      if (count) count.textContent = field.value.length;
    }

    /** Mirror visible fields into the hidden properties on the MAIN line. */
    syncProperties() {
      this.fields.forEach((field) => {
        const key = field.dataset.giftField;
        const property = this.querySelector(`[data-gift-property="${CSS.escape(key)}"]`);
        if (!property) return;
        property.value = field.value;
        property.disabled = !this.checked || field.value.trim() === '';
      });
    }

    dispatchBundleChange() {
      const form = this.productForm;
      if (!form) return;
      const items = Array.from(
        document.querySelectorAll(`[form="${this.formId}"] input[name="bundles"]:checked`)
      ).map((input) => ({
        id: input.value,
        quantity: input.getAttribute('data-min'),
        price: input.getAttribute('data-price')
      }));
      form.dispatchEvent(new CustomEvent('bundle:change', { detail: { items } }));
    }

    onCartAdded() {
      if (!this.checked) return;
      this.checkbox.checked = false;
      this.toggleFields();
      this.syncProperties();
      this.dispatchBundleChange();
    }

    /** Live parity: To / Message / From are required when the box is ticked. */
    missingField() {
      if (!this.checked) return null;
      for (const field of this.fields) {
        if (field.value.trim() === '') return field.dataset.giftField;
      }
      return null;
    }

    fieldErrorMessage(key) {
      if (key === 'To') return 'The To field is required!';
      if (key === 'From') return 'The From field is required!';
      return 'The Message field is required!';
    }

    showFieldError(key) {
      if (this.errorElement) {
        this.errorElement.textContent = this.fieldErrorMessage(key);
        this.errorElement.hidden = false;
      }
      const field = this.fields.find((candidate) => candidate.dataset.giftField === key);
      if (field) field.focus();
    }

    clearFieldError() {
      if (!this.errorElement) return;
      this.errorElement.hidden = true;
    }

    get sessionKey() {
      return 'chummys:gift-modal-shown';
    }

    shouldPrompt() {
      return this.dataset.prompt === 'true' && !!this.modal && !this.checked && !sessionGet(this.sessionKey);
    }

    /**
     * Open the reminder modal through the theme's own pathway ONLY:
     * Concept's ModalElement.show() sets `open` (-> unhides, runs
     * showTransition which adds `active`; CSS keyed on [active] makes it
     * visible — mirroring `.drawer .modal[active]`). Never set attributes by
     * hand: on an un-upgraded element that shows nothing and would swallow
     * the customer's click.
     *
     * @returns {boolean} true only if the modal actually opened — the
     * once-per-session flag is set here and ONLY on success, so a failed
     * show never silently eats a click.
     */
    openModal(submitter) {
      const modal = this.modal;
      if (!modal || typeof modal.show !== 'function') return false;
      this.pendingSubmitter = submitter || null;
      this.returnFocus = submitter || null;
      // <body> is the only ancestor guaranteed not to be transformed, so position:fixed means the screen
      if (modal.parentElement !== document.body) document.body.appendChild(modal);
      // start from whatever the customer already typed on the page
      this.modalFields.forEach((field) => {
        const source = this.fields.find((f) => f.dataset.giftField === field.dataset.giftModalField);
        if (source && !field.value) field.value = source.value;
      });
      this.previewModal();
      this.clearModalError();
      modal.show();
      if (!modal.hasAttribute('open')) {
        this.pendingSubmitter = null;
        return false;
      }
      document.body.classList.add('chummys-gift-open');
      // the once-per-session flag is set when the customer answers (no thanks / close), not on open:
      // a box added without a message asks every time until they have said no once
      const first = this.modalFields[0];
      if (first) setTimeout(() => first.focus({ preventScroll: true }), 350);
      return true;
    }

    previewModal() {
      if (!this.modal) return;
      this.modalFields.forEach((field) => {
        const key = field.dataset.giftModalField;
        const out = this.modal.querySelector(`[data-gift-preview="${key}"]`);
        const value = field.value.trim();
        if (out) {
          if (key === 'Add Gift Tag Message') out.textContent = value || 'Your message will appear here';
          else out.textContent = value || '…';
          out.classList.toggle('is-empty', !value);
        }
        if (key === 'Add Gift Tag Message') {
          const count = this.modal.querySelector('[data-gift-modal-count]');
          if (count) count.textContent = `${field.value.length}/${field.maxLength > 0 ? field.maxLength : 175}`;
        }
      });
    }

    showModalError(message, field) {
      const box = this.modal && this.modal.querySelector('[data-gift-modal-error]');
      if (box) { box.textContent = message; box.hidden = false; }
      if (field) field.focus();
    }

    clearModalError() {
      const box = this.modal && this.modal.querySelector('[data-gift-modal-error]');
      if (box) box.hidden = true;
    }

    closeModal() {
      sessionSet(this.sessionKey); // closing counts as "not now" for this session
      if (typeof this.modal.hide === 'function') {
        this.modal.hide();
      } else {
        this.modal.removeAttribute('open');
        this.modal.hidden = true;
      }
    }

    onModalAdd() {
      // live parity: To / Message / From are all required once a gift tag is going in
      for (const field of this.modalFields) {
        if (field.value.trim() === '') {
          this.showModalError(this.fieldErrorMessage(field.dataset.giftModalField).replace('!', '.'), field);
          return;
        }
      }
      this.clearModalError();
      this.modalFields.forEach((field) => {
        const target = this.fields.find((f) => f.dataset.giftField === field.dataset.giftModalField);
        if (target) {
          target.value = field.value.trim();
          target.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
      if (!this.checkbox.checked) {
        this.checkbox.checked = true;
        this.checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.syncProperties();
      this.returnFocus = null;
      this.closeModal();
      // carry on with the add the customer started — the gift tag goes in the same cart call
      const form = this.productForm;
      if (!form) return;
      try {
        if (this.pendingSubmitter && this.pendingSubmitter.isConnected) form.requestSubmit(this.pendingSubmitter);
        else form.requestSubmit();
      } catch (error) {
        form.requestSubmit();
      }
      this.pendingSubmitter = null;
    }

    onModalSkip() {
      this.returnFocus = null;
      sessionSet(this.sessionKey); // "no thanks": do not ask again this session; the cart still reminds at Check out
      this.closeModal();
      const form = this.productForm;
      if (!form) return;
      // Session flag is set above, so the capture validator lets this pass.
      try {
        if (this.pendingSubmitter && this.pendingSubmitter.isConnected) {
          form.requestSubmit(this.pendingSubmitter);
        } else {
          form.requestSubmit();
        }
      } catch (error) {
        form.requestSubmit();
      }
      this.pendingSubmitter = null;
    }
  }

  /**
   * Single capture-phase validator for both blocks. Runs before Concept's
   * at-target ProductForm handler on BOTH the AJAX and native cart paths,
   * and covers the sticky buy bar (same form).
   */
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    // NOTE: `form.id` is shadowed by the theme's `input[name="id"]` — always
    // read the attribute.
    const formId = form.getAttribute('id');
    if (!formId) return;
    const group = registry.get(formId);
    if (!group) return;

    const veto = () => {
      event.preventDefault();
      event.stopPropagation();
    };

    const picker = group.picker;
    if (picker && picker.isConnected) {
      picker.sync();
      if (!picker.validate()) {
        veto();
        picker.showError();
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      picker.clearError();
    }

    const gift = group.gift;
    if (gift && gift.isConnected) {
      gift.syncProperties();
      const missing = gift.missingField();
      if (missing) {
        veto();
        gift.showFieldError(missing);
        gift.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      gift.clearFieldError();
      if (gift.shouldPrompt()) {
        // Only veto the submit when the modal actually opened — if it cannot
        // be shown (element not upgraded yet), let the add-to-cart proceed so
        // the customer never loses a click.
        if (gift.openModal(event.submitter)) veto();
      }
    }
  }, true);

  customElements.define('brownie-options', BrownieOptions);
  customElements.define('gift-addon', GiftAddon);

  /**
   * Concept ProductForm patch — selling plans + bundles batch add.
   *
   * theme.js prepareFormData moves every non-id/quantity/properties form
   * field — including Recharge's `selling_plan` input — to the TOP LEVEL of
   * the JSON /cart/add payload it builds when any `name="bundles"` checkbox
   * (our gift add-on) is ticked. Shopify ignores top-level `selling_plan` on
   * an `items[]` payload, so a subscription purchase with the gift ticked
   * would silently become a one-time order. Move it onto the MAIN item —
   * Concept pushes the main product LAST, after the bundle items. Covers both
   * the AJAX path and the native cart-page path (postLink2), which share
   * prepareFormData. No-op when no selling plan is selected or no bundle is
   * ticked (prepareFormData is only called with bundles checked).
   */
  /**
   * Concept postLink2 patch — page-cart bundle adds.
   *
   * With cart_type = page and a gift tag ticked, Concept posts a generated
   * form to /cart/add, but its postLink2 only flattens two levels
   * (items[i][key]) — the main line's `properties` object becomes the literal
   * string "[object Object]" and Shopify rejects the add. Flatten every level
   * (items[1][properties][To]) so gift messages survive the page-cart path.
   */
  customElements.whenDefined('product-form').then(() => {
    const utils = window.theme && window.theme.utils;
    if (!utils || typeof utils.postLink2 !== 'function' || utils.postLink2.chummysNestedPatch) return;
    const patched = (path, options) => {
      options = options || {};
      const form = document.createElement('form');
      form.setAttribute('method', options.method || 'post');
      form.setAttribute('action', path);
      const add = (name, value) => {
        if (value === null || value === undefined) return;
        if (typeof value === 'object') {
          Object.keys(value).forEach((key) => add(`${name}[${key}]`, value[key]));
          return;
        }
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = String(value);
        form.appendChild(input);
      };
      const params = options.parameters || {};
      Object.keys(params).forEach((key) => add(key, params[key]));
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    };
    patched.chummysNestedPatch = true;
    utils.postLink2 = patched;
  });

  customElements.whenDefined('product-form').then(() => {
    const ProductFormClass = customElements.get('product-form');
    const proto = ProductFormClass && ProductFormClass.prototype;
    if (!proto || typeof proto.prepareFormData !== 'function') return;
    if (proto.prepareFormData.chummysSellingPlanPatch) return;

    const original = proto.prepareFormData;
    const patched = function (formData) {
      const payload = original.call(this, formData);
      if (
        payload &&
        payload.selling_plan &&
        Array.isArray(payload.items) &&
        payload.items.length > 0
      ) {
        payload.items[payload.items.length - 1].selling_plan = payload.selling_plan;
        delete payload.selling_plan;
      }
      return payload;
    };
    patched.chummysSellingPlanPatch = true;
    proto.prepareFormData = patched;
  });
})();
